'use client';
/**
 * SPM 终端解析与查表工具。
 *
 * 全局框架组件（Header/Footer/侧边导航/面包屑）渲染在所有页面，需求方拍板其
 * B 位固定为 global_pc / global_wap（App WebView 内嵌时为 global_app），与所在
 * 页面无关：用 useGlobalSpmTerminal 按终端取表，再用 getSpmPositionCode 查
 * 「模块 C + 坑位 D」。查无页面/模块/坑位时返回 undefined，调用方不附加编码，
 * 天然兼容表外项。
 *
 * 页面楼层组件（首页/列表/商详楼层，B 位跟随所在页面）仍走
 * useSpmPageTerminal：先按「当前路由 + 终端」解析页面终端，再查表。
 *
 * 终端归属（pc / wap / app）统一由 reporting/spm/terminal.ts 判定：App WebView
 * 内嵌时 B 位上报 <页面>_app 而非 <页面>_wap。
 */
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { getSpmPageCodes, type SpmPageId } from '@/reporting/spm';
import { useSpmTerminalKind } from '@/reporting/spm/spm-dom';
import { resolveSpmTerminalKind } from '@/reporting/spm/terminal';
import { EPlatform } from '@/utils/enums/platform-enum';

/**
 * P0 路由 → 页面终端映射；其余路由返回 null（一切照旧，不附加编码）。
 * 纯函数版供测试与非 hook 场景使用；渲染期请用 useSpmPageTerminal（App 内嵌
 * 判定走 Provider 下发的服务端终端段，SSR 与客户端同值）。
 */
export function resolveSpmPageTerminal(
  pathname: string | null | undefined,
  deviceType: EPlatform,
): SpmPageId | null {
  if (!pathname) return null;
  if (pathname === '/') {
    return `home_${resolveSpmTerminalKind(deviceType)}`;
  }
  if (pathname.startsWith('/goods-detail/')) {
    return `product_detail_${resolveSpmTerminalKind(deviceType)}`;
  }
  return null;
}

/** 当前路由 + 终端对应的页面终端；非 P0 页面为 null。 */
export function useSpmPageTerminal(deviceType: EPlatform): SpmPageId | null {
  const { pathname } = useRouteMetadata();
  const kind = useSpmTerminalKind(deviceType);
  if (!pathname) return null;
  if (pathname === '/') return `home_${kind}`;
  if (pathname.startsWith('/goods-detail/')) return `product_detail_${kind}`;
  return null;
}

/**
 * 查当前页面终端编码表中「模块 C + 坑位 D」的完整编码。
 * 查无页面/模块/坑位均返回 undefined，由调用方决定不附加（表外）。
 */
export function getSpmPositionCode(
  page: SpmPageId | null,
  moduleCode: string,
  position: string,
): string | undefined {
  if (!page) return undefined;
  return getSpmPageCodes(page)[moduleCode]?.[position];
}

export function resolveGlobalSpmTerminal(
  deviceType: EPlatform,
): 'global_pc' | 'global_wap' | 'global_app' {
  return `global_${resolveSpmTerminalKind(deviceType)}`;
}

/**
 * 全局框架组件固定使用的全局终端（global_pc / global_wap / global_app），与所在页面无关。
 * App 内嵌判定优先走 Provider 下发的服务端终端段（SSR 与客户端同值），UA 兜底。
 */
export function useGlobalSpmTerminal(
  deviceType: EPlatform,
): 'global_pc' | 'global_wap' | 'global_app' {
  return `global_${useSpmTerminalKind(deviceType)}`;
}

/** D 位坑位序号统一两位数字（01、02…），与编码表命名一致。 */
export function padSpmPositionIndex(index: number): string {
  return String(index).padStart(2, '0');
}
